import React, { useEffect, useState, useRef } from 'react';

export default function ProjectLightbox({ images = [], index = 0, open = false, onClose = () => {}, onPrev = () => {}, onNext = () => {} }) {

    const ANIM_OUT = 300; // ms
    const ANIM_IN = 300; // ms

    const [visibleIndex, setVisibleIndex] = useState(index);
    const [animStage, setAnimStage] = useState('idle'); // 'idle' | 'out' | 'in' | 'closing'
    const mountedRef = useRef(false);

    // sync visibleIndex when opening for the first time
    useEffect(() => {
        if (open) {
            setVisibleIndex(index);
            setAnimStage('idle');
            mountedRef.current = true;
        }
    }, [open]);

    // key handlers (existing logic)
    useEffect(() => {
        if (!open) return;

        const onKey = (e) => {
            if (e.key === 'Escape') handleClose();
            if (e.key === 'ArrowLeft') handlePrev();
            if (e.key === 'ArrowRight') handleNext();
        };

        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open]);

    // animate when index prop changes
    useEffect(() => {
        if (!open) return;
        if (index === visibleIndex) return;
        if (animStage === 'out' || animStage === 'in' || animStage === 'closing') return; // already animating

        setAnimStage('out');
        const t1 = setTimeout(() => {
            setVisibleIndex(index);
            setAnimStage('in');
            const t2 = setTimeout(() => setAnimStage('idle'), ANIM_IN);
            return () => clearTimeout(t2);
        }, ANIM_OUT);

        return () => clearTimeout(t1);
    }, [index, open]);

    const isBusy = animStage === 'out' || animStage === 'in' || animStage === 'closing';

    const handleClose = (e) => {
        if (e && e.stopPropagation) e.stopPropagation();
        if (isBusy) return;
        setAnimStage('closing');
        setTimeout(() => {
            // appeler onClose après la fin de l'animation pour que le parent puisse restaurer le scroll
            onClose();
            setAnimStage('idle');
        }, ANIM_OUT + ANIM_IN);
    };

    const handlePrev = (e) => {
        if (e && e.stopPropagation) e.stopPropagation();
        if (isBusy) return;
        onPrev();
    };

    const handleNext = (e) => {
        if (e && e.stopPropagation) e.stopPropagation();
        if (isBusy) return;
        onNext();
    };

    if (!open) return null;

    const current = images && images[visibleIndex] ? images[visibleIndex].img || images[visibleIndex] : null;

    const imgClass = `lightbox-image ${animStage === 'out' ? 'fade-out' : ''} ${animStage === 'in' ? 'fade-in' : ''} ${animStage === 'closing' ? 'fade-out' : ''}`.trim();

    return (
        <div className="project-lightbox" role="dialog" aria-modal="true" aria-label="Project images viewer">
            <div className="lightbox-overlay" onClick={(e) => { if (e.target === e.currentTarget) handleClose(e); }}>
                <button className="lightbox-close" aria-label="Close" onClick={handleClose} disabled={isBusy}>×</button>

                <div className="lightbox-inner">
                    <a className={`arrow-container arrow-prev ${isBusy ? 'disabled' : ''}`} aria-label="Previous image" onClick={handlePrev} disabled={isBusy}>
                        <svg className="arrow-down" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                            <polygon points="11.293 4.707 17.586 11 4 11 4 13 17.586 13 11.293 19.293 12.707 20.707 21.414 12 12.707 3.293 11.293 4.707"/>
                        </svg>
                    </a>

                    <div className="lightbox-image-wrap">
                        {current ? (
                            <img src={current} alt={`project-large-${visibleIndex}`} loading="eager" className={imgClass} />
                        ) : null}
                    </div>

                    <a className={`arrow-container arrow-next ${isBusy ? 'disabled' : ''}`} aria-label="Next image" onClick={handleNext} disabled={isBusy}>
                        <svg className="arrow-down" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                            <polygon points="11.293 4.707 17.586 11 4 11 4 13 17.586 13 11.293 19.293 12.707 20.707 21.414 12 12.707 3.293 11.293 4.707"/>
                        </svg>
                    </a>
                </div>

                <div className="lightbox-counter" aria-hidden="true">{visibleIndex + 1} / {images.length}</div>
            </div>
        </div>
    );
}
